import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, message, Tabs, Button, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import Api from '../../Network';
import PermissionsTable from '../../Component/PermissionsTable';

const { TabPane } = Tabs;

interface ModelModalProps {
  visible: boolean;
  mode: 'create' | 'edit';
  model: any;
  onClose: () => void;
  onSuccess: () => void;
}

const ModelModal: React.FC<ModelModalProps> = ({ visible, mode, model, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // Fetch model details if editing
  const { data: modelDetails } = useQuery({
    queryKey: [`/api/admin/models/${model?.id}`],
    queryFn: () => Api.get(`/api/admin/models/${model?.id}`),
    enabled: visible && mode === 'edit' && !!model?.id,
  });

  // Create model mutation
  const createModelMutation = useMutation({
    mutationFn: (data: any) => Api.post('/api/admin/models', data),
    onSuccess: () => {
      message.success(t('models.addSuccess', 'Model created successfully'));
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 422) {
        const errors = error?.response?.data?.errors || {};
        Object.keys(errors).forEach((key) => {
          form.setFields([
            {
              name: key,
              errors: Array.isArray(errors[key]) ? errors[key] : [errors[key]],
            },
          ]);
        });
      } else {
        const errorMessage =
          error?.response?.data?.msg || t('common.error', 'Error creating model');
        message.error(errorMessage);
      }
    },
  });

  // Update model mutation
  const updateModelMutation = useMutation({
    mutationFn: (data: any) => Api.put(`/api/admin/models/${model?.id}`, data),
    onSuccess: () => {
      message.success(t('models.updateSuccess', 'Model updated successfully'));
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 422) {
        const errors = error?.response?.data?.errors || {};
        Object.keys(errors).forEach((key) => {
          form.setFields([
            {
              name: key,
              errors: Array.isArray(errors[key]) ? errors[key] : [errors[key]],
            },
          ]);
        });
      } else {
        const errorMessage =
          error?.response?.data?.msg || t('common.error', 'Error updating model');
        message.error(errorMessage);
      }
    },
  });

  // Assign permissions mutation
  const assignPermissionsMutation = useMutation({
    mutationFn: ({ modelId, permissionIds }: { modelId: number; permissionIds: number[] }) =>
      Api.post(`/api/admin/models/${modelId}/assign-permissions`, { permissions: permissionIds }),
    onSuccess: () => {
      message.success(t('models.permissionsAssigned', 'Permissions assigned successfully'));
      onSuccess();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.msg || t('common.error', 'Error assigning permissions');
      message.error(errorMessage);
    },
  });

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      if (mode === 'create') {
        await createModelMutation.mutateAsync(values);
      } else if (mode === 'edit') {
        await updateModelMutation.mutateAsync(values);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionsSubmit = async () => {
    const permissionIds = form.getFieldValue('permissions') || [];
    if (mode === 'edit' && model?.id) {
      setLoading(true);
      try {
        await assignPermissionsMutation.mutateAsync({
          modelId: model.id,
          permissionIds,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePermissionsChange = (permissionIds: number[]) => {
    form.setFieldsValue({ permissions: permissionIds });
  };

  const handleCancel = () => {
    form.resetFields();
    setActiveTab('basic');
    onClose();
  };

  // Set form values when editing
  useEffect(() => {
    if (visible && mode === 'edit') {
      const modelData = modelDetails?.data?.data?.model || model;
      if (modelData) {
        const permissionIds = modelData.permissions?.map((p: any) => p.id) || [];

        form.setFieldsValue({
          key: modelData.key,
          name_en: modelData.name_en,
          name_ar: modelData.name_ar,
          permissions: permissionIds,
        });
      }
    } else if (visible && mode === 'create') {
      form.resetFields();
    }
  }, [visible, modelDetails, mode, form, model]);

  return (
    <Modal
      title={
        mode === 'create'
          ? t('models.createTitle', 'Create Menu Model')
          : t('models.editTitle', 'Edit Menu Model')
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={1400}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={t('models.basicInfo', 'Basic Information')} key="basic">
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="key"
              label={t('models.form.key', 'Key')}
              rules={[
                { required: true, message: t('validation.required', 'This field is required') },
              ]}
            >
              <Input
                placeholder={t('models.form.keyPlaceholder', 'Enter route key (e.g., /dashboard)')}
              />
            </Form.Item>

            <Form.Item
              name="name_en"
              label={t('models.form.nameEn', 'Name (English)')}
              rules={[
                { required: true, message: t('validation.required', 'This field is required') },
              ]}
            >
              <Input
                placeholder={t('models.form.nameEnPlaceholder', 'Enter model name in English')}
              />
            </Form.Item>

            <Form.Item
              name="name_ar"
              label={t('models.form.nameAr', 'Name (Arabic)')}
              rules={[
                { required: true, message: t('validation.required', 'This field is required') },
              ]}
            >
              <Input
                placeholder={t('models.form.nameArPlaceholder', 'Enter model name in Arabic')}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleCancel}>{t('common.cancel', 'Cancel')}</Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {mode === 'create' ? t('common.add', 'Add') : t('common.update', 'Update')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </TabPane>

        {mode === 'edit' && (
          <TabPane tab={t('models.permissions', 'Permissions')} key="permissions">
            <Form form={form} layout="vertical" onFinish={handlePermissionsSubmit}>
              <Form.Item
                name="permissions"
                label={t('models.form.assignPermissions', 'Assign Permissions')}
              >
                <PermissionsTable
                  selectedPermissions={form.getFieldValue('permissions') || []}
                  onChange={handlePermissionsChange}
                  initialPermissions={
                    modelDetails?.data?.data?.model?.permissions || model?.permissions || []
                  }
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={handleCancel}>{t('common.cancel', 'Cancel')}</Button>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    {t('models.assignPermissions', 'Assign Permissions')}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </TabPane>
        )}
      </Tabs>
    </Modal>
  );
};

export default ModelModal;
